import React, {useEffect, useLayoutEffect, useRef} from "react";
import {createPortal} from "react-dom";

export type PopupAnchor =
    | { type: "element"; el: HTMLElement }
    | { type: "point"; x: number; y: number }
    | null;

export type PopupPlacement =
    | "bottom-start" | "bottom-end"
    | "top-start" | "top-end"
    | "right-start" | "left-start";

export type PopupProps = {
    open: boolean;
    onClose: () => void;
    anchor: PopupAnchor;
    placement?: PopupPlacement;
    offset?: number;              // gap between anchor and popup
    className?: string;           // extra classes for the panel
    children: React.ReactNode;
    // a11y
    role?: "menu" | "dialog" | "listbox" | "tooltip";
    labelledById?: string;        // aria-labelledby target id
    initialFocusRef?: React.RefObject<HTMLElement>; // focus on open
};

const portalRoot = document.body;

export const Popup: React.FC<PopupProps> = ({
                                                open,
                                                onClose,
                                                anchor,
                                                placement = "bottom-start",
                                                offset = 8,
                                                className = "",
                                                children,
                                                role = "menu",
                                                labelledById,
                                                initialFocusRef,
                                            }) => {
    const panelRef = useRef<HTMLDivElement | null>(null);
    const coords = useRef<{ left: number; top: number }>({left: 0, top: 0});

    const compute = () => {
        if (!anchor) return {left: 0, top: 0};
        let left = 0, top = 0;

        const placeFromRect = (r: DOMRect) => {
            switch (placement) {
                case "bottom-start":
                    return {left: r.left, top: r.bottom + offset};
                case "bottom-end":
                    return {left: r.right, top: r.bottom + offset};
                case "top-start":
                    return {left: r.left, top: r.top - offset};
                case "top-end":
                    return {left: r.right, top: r.top - offset};
                case "right-start":
                    return {left: r.right + offset, top: r.top};
                case "left-start":
                    return {left: r.left - offset, top: r.top};
            }
        };

        if (anchor.type === "element") {
            const r = anchor.el.getBoundingClientRect();
            ({left, top} = placeFromRect(r)!);
        } else {
            left = anchor.x;
            top = anchor.y + offset;
        }

        const panel = panelRef.current;
        const vw = window.innerWidth;
        const vh = window.innerHeight;
        if (panel) {
            const w = panel.offsetWidth;
            const h = panel.offsetHeight;

            if (left + w > vw - 4) left = Math.max(4, vw - w - 4);
            if (left < 4) left = 4;

            if (top + h > vh - 4) top = Math.max(4, vh - h - 4);
            if (top < 4) top = 4;
        }

        return {left, top};
    };

    useLayoutEffect(() => {
        if (!open || !anchor) return;
        coords.current = compute();

        const onResize = () => {
            coords.current = compute();
            forceRerender(panelRef);
        };
        const onScroll = () => {
            coords.current = compute();
            forceRerender(panelRef);
        };

        window.addEventListener("resize", onResize);
        window.addEventListener("scroll", onScroll, true);
        return () => {
            window.removeEventListener("resize", onResize);
            window.removeEventListener("scroll", onScroll, true);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, anchor, placement, offset]);

    useEffect(() => {
        if (!open) return;
        const onDocMouseDown = (e: MouseEvent) => {
            if (panelRef.current && !panelRef.current.contains(e.target as Node)) onClose();
        };
        const onKey = (e: KeyboardEvent) => {
            if (e.key === "Escape") onClose();
        };

        document.addEventListener("mousedown", onDocMouseDown);
        document.addEventListener("keydown", onKey);
        return () => {
            document.removeEventListener("mousedown", onDocMouseDown);
            document.removeEventListener("keydown", onKey);
        };
    }, [open, onClose]);

    useEffect(() => {
        if (open) {
            const el = initialFocusRef?.current ?? panelRef.current;
            el?.focus();
        }
    }, [open, initialFocusRef]);

    useEffect(() => {
        if (open && anchor) {
            // let React paint first, then compute again
            requestAnimationFrame(() => {
                coords.current = compute();
                if (panelRef.current) {
                    panelRef.current.style.left = coords.current.left + "px";
                    panelRef.current.style.top = coords.current.top + "px";
                }
            });
        }
    }, [open, anchor, placement, offset]);

    if (!open || !anchor) return null;

    const style: React.CSSProperties = {
        left: coords.current.left,
        top: coords.current.top,
        fontFamily: 'var(--manager-acrom-font-family), sans-serif',
        fontWeight: 500,
    };

    return createPortal(
        <div
            ref={panelRef}
            style={style}
            tabIndex={-1}
            role={role}
            aria-labelledby={labelledById}
            data-state="open"
            className={
                [
                    "fixed z-[10000] rounded-lg border border-gray-100 bg-white",
                    "shadow-lg dark:bg-gray-800 dark:border-gray-700",
                    "outline-none",
                    // animations: scale+fade
                    "data-[state=open]:animate-in data-[state=open]:fade-in data-[state=open]:zoom-in-95",
                    "data-[state=closed]:animate-out data-[state=closed]:fade-out data-[state=closed]:zoom-out-95",
                    "max-h-[70vh] overflow-auto",
                    "text-melon-black",
                    className
                ].join(" ")
            }
        >
            {children}
        </div>,
        portalRoot
    );
};

function forceRerender(ref: React.RefObject<HTMLElement>) {
    if (!ref.current) return;
    // flip data-state to retrigger animation-less style updates
    const el = ref.current;
    el.dataset.state = "open";
}
